import React, { useState } from 'react';
import clsx from 'clsx';
import { CardContainer } from '@/components/dashboard';
import { Header } from './Header';
import SimpleTable from '@/components/common/tables/simpleTable';
import { ClientsFilter } from '../case-clients/filters';
import {
	Avatar,
	DotStatus,
	EmptyUsers,
	Icon,
	Typography,
} from '@/components/common';
import { DateRangeFilter } from '@/components/screens/dashboard/table-filters';
import { ClientStatus } from './filters/ClientStatus';
import { DocumentType } from './filters/DocumentType';
import { DocumentStatus } from './filters/DocumentStatus';
import Icons from '@/const/icons';
import { TooltipComponent } from '@/components/common/tooltip';
import { useRouter } from 'next/router';
import { CLIENTS_DOCUMENTS } from '@/api';
import { caseDocumentsService } from '@/api/caseDocuments';
import { useNotification } from '@/hooks';
import {
	ItemClientDocument,
	ClientDocument,
	DownloadDocumentsParams,
	IClientDocuments,
	DocumentsToDownload,
} from '@/interfaces';
import { downloadPDFByUrl, getFormattedTableDate } from '@/lib';
import { EmptyStateFilter } from '../../common/EmptyStateFilter';
import { useQuery } from '@tanstack/react-query';
import { ConfirmModal } from '@/components/modals/common/ConfirmModal';

interface TableData extends ItemClientDocument {
	isActive: boolean;
}

interface Props {
	id: string;
	caseName: string;
}

export const ClientDocuments: React.FC<Props> = ({ id, caseName }) => {
	const router = useRouter();
	const { toastSuccess, toastError } = useNotification();
	const [isDownload, setIsDownload] = React.useState(false);
	const [isModalConfirmOpen, setIsModalConfirmOpen] = useState(false);
	const [dataDownload, setDataDownload] = React.useState<{
		allSelected: boolean;
		documents: any[];
		total: number;
	}>({
		allSelected: false,
		documents: [],
		total: 0,
	});

	const {
		search,
		client_status,
		document_status,
		dateFrom,
		dateTo,
		document_types,
		clientId,
		received_start_date,
		received_end_date,
		sent_start_date,
		sent_end_date,
	} = router.query;

	const { data: clientData, isLoading } = useQuery({
		queryKey: [
			CLIENTS_DOCUMENTS,
			search,
			client_status,
			document_status,
			document_types,
			dateFrom,
			dateTo,
			id,
			clientId,
			received_start_date,
			received_end_date,
			sent_start_date,
			sent_end_date,
		],
		queryFn: () =>
			caseDocumentsService.getListDocuments({
				page: String(1),
				limit: '10',
				search: search as string,
				client_status: client_status as string,
				document_status: document_status as string,
				document_types: document_types as string,
				case_id: id,
				client_id: clientId as string,
				received_start_date: received_start_date as string,
				received_end_date: received_end_date as string,
				sent_start_date: sent_start_date as string,
				sent_end_date: sent_end_date as string,
			}),
	});

	const items = React.useMemo(
		() =>
			clientData?.data.items?.map((item) => ({
				...item,
				documents: item.documents.map((d) => ({
					...d,
					application_id: item.application_id,
				})),
				isActive: false,
			})) ?? [],
		[clientData]
	);

	const total = clientData?.data.total ?? 0;
	const totalItems = clientData?.data.totalItems ?? 0;

	const handleSelectionChange = (selected: {
		rows: any[];
		subRows: any[];
		allSelected: boolean;
	}) => {
		const isAllSelected = selected.allSelected && !clientId && !search;
		const documents: IClientDocuments[] = [];

		selected.subRows.forEach((list) => {
			const item = documents.find(
				(d) => d.application_id === list.application_id
			);
			const newDoc = {
				document_id: list.document_id,
				document_type: list.document_type,
			};
			item
				? item.docs.push(newDoc)
				: documents.push({
						application_id: list.application_id,
						docs: [newDoc],
				  });
		});

		setDataDownload({
			allSelected: isAllSelected && documents.length > 0,
			documents: documents,
			total: documents.length,
		});
	};

	const getData = (): DownloadDocumentsParams => {
		let request: DownloadDocumentsParams = {
			select_all: dataDownload.allSelected,
			...(dataDownload.allSelected &&
				client_status && {
					client_status: (client_status as string).split(','),
				}),
			...(dataDownload.allSelected &&
				document_status && {
					document_status: (document_status as string).split(','),
				}),
			...(dataDownload.allSelected &&
				document_types && {
					document_types: (document_types as string).split(','),
				}),
			...(dataDownload.allSelected &&
				sent_start_date && { sent_start_date: sent_start_date }),
			...(dataDownload.allSelected &&
				sent_end_date && { sent_end_date: sent_end_date }),
			...(dataDownload.allSelected &&
				received_start_date && { received_start_date: received_start_date }),
			...(dataDownload.allSelected &&
				received_end_date && { received_end_date: received_end_date }),
		};

		if (!dataDownload.allSelected) {
			request = {
				...request,
				clients_document: dataDownload.documents,
			};
		}

		return request;
	};

	const handleDownloadDocuments = async () => {
		try {
			setIsDownload(true);
			const body = getData();
			const response = await caseDocumentsService.getDocumentsToDownload(
				id,
				body
			);
			downloadFiles(response.data);
		} catch (error) {
			toastError('An error has occurred. Please try again.');
			setIsDownload(false);
		}
	};

	const chunksArray = (array: any[], size: number) => {
		const chunks = [];
		for (let i = 0; i < array.length; i += size) {
			const element = array.slice(i, i + size);
			chunks.push(element);
		}

		return chunks;
	};

	const downloadFiles = async (documents: DocumentsToDownload[]) => {
		const chunks = chunksArray(documents, 100);
		for await (const [index, chunk] of chunks.entries()) {
			try {
				const request = {
					folderZipName:
						documents.length <= 100
							? caseName
							: `${caseName} - Part ${index + 1}`,
					documents: chunk,
				};
				const res = await caseDocumentsService.downloadDocuments(request);

				if (res.downloadUrl) {
					const link = document.createElement('a');
					link.href = res.downloadUrl;
					link.download = '';
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					toastSuccess('The documents have been downloaded successfully');
				}
			} catch (error) {
				toastError('An error has occurred. Please try again.');
			} finally {
				await new Promise((resolve) => setTimeout(resolve, 2000));
			}
		}
		setIsDownload(false);
	};

	const appliedFilters =
		search ||
		client_status ||
		document_status ||
		dateFrom ||
		dateTo ||
		document_types ||
		clientId;

	return (
		<>
			<CardContainer
				title="Client Documents"
				icon={Icons.documentGreen}
				classNameContent={clsx('!min-h-[250px] !h-full overflow-hidden', {})}
			>
				<Header
					total={total}
					totalItems={totalItems}
					isDownload={isDownload}
					countDownload={dataDownload.total}
					downloadEnabled={!(dataDownload.total > 0)}
					onClickDownload={() => setIsModalConfirmOpen(true)}
				/>
				<SimpleTable<TableData, ClientDocument>
					hideRowActions
					hasCheckboxes={true}
					hasPagination={false}
					defaultExpandAll={true}
					classNameContainer={clsx('border-none shadow-none mt-6 3xl:h-[68vh]')}
					classNameHead={clsx('bg-white !opacity-100')}
					classNameHeader={clsx(
						'w-full lg:grid lg:grid-cols-[2fr,1fr,repeat(5,_1fr)] 2xl:grid-cols-[2fr,1fr,repeat(5,_1fr)] items-center'
					)}
					classNameRows="text-black lg:grid lg:grid-cols-[1.5fr,1fr,repeat(5,_1fr)] 2xl:grid-cols-[1.8fr,1fr,repeat(5,_1fr)] 3xl:grid-cols-[2fr,1fr,repeat(5,_1fr)] items-center cursor-pointer"
					classNameSubRows="lg:grid lg:grid-cols-[1.5fr,1fr,repeat(5,_1fr)] 2xl:grid-cols-[1.8fr,1fr,repeat(5,_1fr)] 3xl:grid-cols-[2fr,1fr,repeat(5,_1fr)]"
					classNameSubColumn="!py-1"
					onSelectionChange={handleSelectionChange}
					totalPages={0}
					subRowsAccessor={(row) => row.documents || []}
					subRowEmptyMessage="No documents to display."
					isCheckboxDisabled={isDownload}
					columns={[
						{
							header: (
								<ClientsFilter caseId={id as string} disabled={isDownload} />
							),
							content: (instance) => (
								<div className="flex items-center gap-x-2 min-w-0 overflow-hidden">
									<Avatar
										photoUrl={instance.profile_picture || ''}
										size="small"
										firstname={`${instance.firstname}`}
										lastname={`${instance.lastname}`}
										className="flex-shrink-0"
									/>
									<span className="truncate min-w-0 px-1">
										{instance.firstname
											? `${instance.firstname} ${instance.lastname}`
											: 'N/N'}
									</span>
								</div>
							),
						},
						{
							header: <ClientStatus disabled={isDownload} />,
							content: (instance) => {
								const getStatusColor = () => {
									switch (instance.client_status) {
										case 'Missing':
											return 'orange';
										case 'All Received':
											return 'green';
										default:
											return 'grey';
									}
								};

								return (
									<div className="flex justify-start items-center gap-2 min-w-0">
										<DotStatus color={getStatusColor()} />
										<Typography type="regular-14" className="truncate min-w-0">
											{instance.client_status}
										</Typography>
									</div>
								);
							},
						},
						// {
						// 	header: (
						// 		<DateRangeFilter
						// 			title="Status Date"
						// 			disabled={isDownload}
						// 			restrictToPast
						// 		/>
						// 	),
						// 	content: (instance) => {
						// 		const formattedDate = instance.status_date
						// 			? getFormattedTableDate(instance.status_date)
						// 			: '';
						// 		return (
						// 			<div className="flex justify-between items-center gap-2">
						// 				<Typography type="regular-14">{formattedDate}</Typography>
						// 			</div>
						// 		);
						// 	},
						// },
						{
							header: (
								<DateRangeFilter
									title="Date Received"
									disabled={isDownload}
									restrictToPast
									name="dateReceived"
									dateFromLabel="received_start_date"
									dateToLabel="received_end_date"
									classNameField="!min-h-0"
								/>
							),
							content: () => {
								return <></>;
							},
						},
						{
							header: (
								<DateRangeFilter
									title="Date Sent"
									disabled={isDownload}
									restrictToPast
									name="dateSent"
									dateFromLabel="sent_start_date"
									dateToLabel="sent_end_date"
									classNameField="!min-h-0"
								/>
							),
							content: () => {
								return <></>;
							},
						},
						{
							header: <DocumentType disabled={isDownload} />,
							content: () => <></>,
						},
						{
							header: <DocumentStatus disabled={isDownload} />,
							content: (instance) => (
								<Typography
									type="regular-14"
									className={clsx('pl-3', {
										'text-main-orange':
											instance.document_status.toLowerCase() === 'pending',
										'text-main-green':
											instance.document_status.toLowerCase() === 'received' ||
											instance.document_status.toLowerCase() === 'revised' ||
											instance.document_status.toLowerCase() ===
												'signed, received',
									})}
								>
									{instance.document_status}
								</Typography>
							),
						},
						{
							header: 'Actions',
							content: () => <></>,
						},
					]}
					subColumns={[
						{
							header: '',
							content: (documents) => (
								<div className="flex items-center gap-2">
									<Icon
										src={Icons.documentText}
										className="!size-6 text-primary shrink-0"
									/>
									<Typography type="regular-14">
										{documents.document_name}
									</Typography>
								</div>
							),
						},
						{
							header: '',
							content: () => <></>,
						},
						{
							header: '',
							content: (instance) => {
								const formattedDate = instance.date_received
									? getFormattedTableDate(instance.date_received)
									: '';
								return (
									<div className="flex justify-between items-center gap-2">
										<Typography type="regular-14">{formattedDate}</Typography>
									</div>
								);
							},
						},
						{
							header: '',
							content: (instance) => {
								const formattedDate = instance.date_sent
									? getFormattedTableDate(instance.date_sent)
									: '';
								return (
									<div className="flex justify-between items-center gap-2">
										<Typography type="regular-14">{formattedDate}</Typography>
									</div>
								);
							},
						},
						{
							header: '',
							content: (documents) => (
								<Typography type="regular-14" className="capitalize">
									{documents.document_type.toLowerCase().replaceAll('_', ' ')}
								</Typography>
							),
						},
						{
							header: '',
							content: (documents) => (
								<Typography
									type="regular-14"
									className={clsx({
										'text-main-orange': !documents.client_task_end_date,
										'text-main-green': !!documents.client_task_end_date,
									})}
								>
									{`${documents.client_task_end_date ? 'Received' : 'Pending'}`}
								</Typography>
							),
						},
						{
							header: '',
							content: (documents) => (
								<TooltipComponent
									align="end"
									label={'Download document'}
									classNameLabel="font-semibold !text-left"
									className="max-w-[184px]"
									trigger={
										<button
											disabled={!documents.url}
											onClick={() => {
												if (documents.url)
													downloadPDFByUrl(
														documents.url,
														`${documents.document_type}-${documents.document_id}`
													);
											}}
											className={clsx({
												'!cursor-not-allowed': !documents.url,
											})}
										>
											<Icon
												src={Icons.documentDownloadThin}
												className="!size-6"
											/>
										</button>
									}
								/>
							),
						},
					]}
					isLoading={isLoading}
					rows={items as TableData[]}
					tableEmptyComponent={
						<>
							{appliedFilters ? (
								<div className="lg:mt-20 lg:mb-40">
									<EmptyStateFilter
										icon={Icons.filterEmptyStateBgGray}
										iconClassName="w-[175px] h-[140px]"
										message={'No results found.'}
										subMessage={
											'Please try another search or adjust your filtering.'
										}
									/>
								</div>
							) : (
								<EmptyUsers />
							)}
						</>
					}
				/>
			</CardContainer>
			<ConfirmModal
				title="Ready to download?"
				titleConfirm="Download"
				description={
					<Typography type="regular-16">
						You&apos;re about to download data for <br />
						<b>{dataDownload.total}</b> of your <b>{totalItems}</b> clients
					</Typography>
				}
				isOpen={isModalConfirmOpen}
				setIsOpen={setIsModalConfirmOpen}
				onConfirm={handleDownloadDocuments}
			/>
		</>
	);
};
